/**
 * Ce que la page Bridge DÉDUIT de ce que le serveur lui rend. Aucun appel ici.
 *
 * POURQUOI CE FICHIER EST SÉPARÉ DES COMPOSANTS
 *
 * Les décisions de la page — quelle étape est faite, qui doit agir, quand montrer la
 * migration — vivaient dans le JSX, mêlées au rendu. On ne pouvait les éprouver qu'en
 * ouvrant un navigateur. Ici elles se testent en millisecondes, et `bridge-etat.test.js`
 * les garde.
 *
 * QUI DOIT AGIR, ET POURQUOI C'EST LA COLONNE LA PLUS UTILE
 *
 * Les trois conditions du raccordement se réparent à trois endroits, et deux d'entre
 * eux ne sont pas chez nous. Un « non raccordé » sans destinataire fait chercher la
 * panne dans nos clés pendant qu'on attend un certificat de Survoltage.
 */

export const NOUS = 'nous';
export const EUX = 'eux';

/** Les variables d'environnement du canal Survoltage, dites en français.
 *  Le nom technique reste affiché à côté, en petit : c'est lui qu'on cherchera dans
 *  le fichier d'environnement. */
const MANQUES = {
  SURVOLTAGE_BRIDGE_ACTIF: { libelle: "l'activation du canal", chez: NOUS },
  SURVOLTAGE_MQTT_PROVIDER: { libelle: 'le nom de provider qui entre dans nos topics', chez: EUX },
  SURVOLTAGE_MQTT_CERT: { libelle: 'le certificat client', chez: EUX },
  SURVOLTAGE_MQTT_KEY: { libelle: 'la clé privée du certificat', chez: EUX },
  SURVOLTAGE_MQTT_CA: { libelle: "l'autorité de certification", chez: EUX },
  SURVOLTAGE_MQTT_HOST: { libelle: "l'adresse de leur broker", chez: EUX },
  SURVOLTAGE_API_LOGIN: { libelle: 'le compte API partenaire', chez: EUX },
  SURVOLTAGE_API_PASSWORD: { libelle: 'le mot de passe API', chez: EUX },
  SURVOLTAGE_COGNITO_CLIENT_ID: { libelle: "l'identifiant Cognito", chez: EUX },
  SURVOLTAGE_OFFER_ID: { libelle: "l'identifiant d'offre", chez: EUX },
};

/** Une variable inconnue garde son nom : inventer un libellé serait pire que de
 *  montrer le vrai. */
export const libelleManque = (variable) => MANQUES[variable]?.libelle || variable;

/** « 1 actif », « 2 actifs », « 0 actif » : la règle française, pas « 1 actifs ». */
export const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

function etapeCanal(synthese, agregateur) {
  const base = { cle: 'canal', titre: 'Canal de commande', onglet: null };
  if (!synthese) return { ...base, ok: null, quiAgit: null, resume: '' };
  if (synthese.peut_agir) {
    return { ...base, ok: true, quiAgit: null,
             resume: agregateur === 'survoltage' ? 'MQTT branché' : 'bridge HTTP ouvert' };
  }
  if (agregateur !== 'survoltage') {
    // Sans canal documenté chez eux, leur canal EST notre bridge HTTP : il existe dès
    // qu'une clé leur est donnée. Attendre quelque chose d'eux serait faux.
    return { ...base, ok: false, quiAgit: NOUS, onglet: 'acces',
             resume: "s'ouvre avec la première clé" };
  }
  const manques = synthese.manques || [];
  const chezEux = manques.filter((v) => (MANQUES[v]?.chez || EUX) === EUX);
  return {
    ...base, ok: false,
    quiAgit: chezEux.length ? EUX : NOUS,
    resume: manques.length
      ? `il manque ${manques.map(libelleManque).join(', ')}`
      : 'inerte',
    manques,
  };
}

/**
 * Les trois étapes, dans l'ordre où elles se réparent.
 *
 * `ok: null` veut dire « on ne sait pas » (synthèse absente) : une étape ne s'affiche
 * jamais faite sur la foi d'une lecture qui n'a pas eu lieu.
 */
export function etapesRaccordement(synthese, agregateur) {
  const c = synthese?.compteurs || {};
  const inconnu = !synthese;
  const acces = {
    cle: 'acces', titre: 'Accès', onglet: 'acces',
    ok: inconnu ? null : Boolean(synthese.peut_appeler),
    quiAgit: inconnu || synthese.peut_appeler ? null : NOUS,
    resume: inconnu ? '' : (synthese.peut_appeler
      ? `${pluriel(c.cles, 'clé active', 'clés actives')}` : 'aucune clé'),
  };
  const perimetre = {
    cle: 'perimetre', titre: 'Périmètre', onglet: 'perimetre',
    ok: inconnu ? null : Boolean(synthese.a_du_perimetre),
    quiAgit: inconnu || synthese.a_du_perimetre ? null : NOUS,
    resume: inconnu ? '' : (synthese.a_du_perimetre
      ? pluriel(c.actifs_declares, 'actif déclaré', 'actifs déclarés')
      : 'aucun actif déclaré'),
  };
  return [acces, etapeCanal(synthese, agregateur), perimetre];
}

/* --- La migration du périmètre ----------------------------------------------------- */

export const estMigrationEnAttente = (simulation) => Boolean(simulation?.a_declarer > 0);

/** Les chiffres de la simulation, rangés pour l'écran : par agrégateur, du plus gros au
 *  plus petit, et les ignorés AVEC leur motif — « 12 ignorés » ne se corrige pas. */
export function resumerMigration(simulation) {
  const tri = (objet) => Object.entries(objet || {}).sort((a, b) => b[1] - a[1]);
  const libelles = simulation?.motifs_libelles || {};
  return {
    parAgregateur: tri(simulation?.par_agregateur),
    ignores: tri(simulation?.motifs).map(([motif, n]) => [libelles[motif] || motif, n]),
  };
}

/* --- L'exclusivité définitive (07/10/2026) ------------------------------------------ */

/** Dit AVANT le clic ce que le serveur fera respecter : une déclaration ne se défait
 *  pas, et l'actif ne pourra plus jamais aller chez un autre agrégateur. */
export const AVERTISSEMENT_DEFINITIF = "Une déclaration est définitive : cet actif ne "
  + "pourra plus jamais être déclaré à un autre agrégateur. Un retrait reste possible, "
  + "avec un motif, mais l'actif restera attaché à cet agrégateur.";

// La consigne de l'onglet Perimetre quand rien n'est declare. Elle promettait « un par un
// ou par lot confirme » : la declaration par lot a disparu avec C1, et chaque declaration
// est definitive (recette navigateur du 15/10).
export const CONSIGNE_AUCUN_DECLARE = 'Déclarez des actifs depuis la liste, un par un : '
  + 'chaque déclaration est définitive.';

const capitaliser = (nom) => `${nom.charAt(0).toUpperCase()}${nom.slice(1)}`;

export const libelleVerrou = (agregateur) => (
  agregateur ? `déjà déclaré à ${capitaliser(agregateur)}` : '');

/** Les candidats à montrer : les déclarables, PUIS ceux verrouillés ailleurs, grisés.
 *  Les cacher ferait chercher un équipement qu'on sait exister ; un type sans
 *  équivalent chez un agrégateur reste masqué, comme avant. */
export const ranger = (candidats) => [
  ...(candidats || []).filter((c) => c.declarable),
  ...(candidats || []).filter((c) => !c.declarable && c.verrouille_chez),
];

/** Ce qui peut encore être déclaré, un par un : jamais un actif verrouillé. Il n'y a plus
 *  de sélection multiple (08/10/2026) : un clic ne verrouille jamais tout un parc. */
export const declarables = (candidats) => (candidats || [])
  .filter((c) => c.declarable && !c.verrouille_chez);

/* --- Le journal -------------------------------------------------------------------- */

export const EN_COURS = 'EN_COURS';
export const FILTRES_JOURNAL = ['tous', 'COMPLETED', 'APPLIED', 'PENDING', 'FAILED', 'REJECTED', 'REMPLACE', EN_COURS];

/** Un ordre ouvert n'a pas encore de statut : il est en cours, pas « inconnu ». */
const statutDe = (ligne) => ligne?.statut || EN_COURS;

export function compterParStatut(lignes) {
  const comptes = {
    tous: lignes.length, COMPLETED: 0, APPLIED: 0, PENDING: 0, FAILED: 0, REJECTED: 0,
    REMPLACE: 0, [EN_COURS]: 0,
  };
  lignes.forEach((l) => { comptes[statutDe(l)] = (comptes[statutDe(l)] || 0) + 1; });
  return comptes;
}

export const filtrerJournal = (lignes, filtre) => (
  filtre === 'tous' ? lignes : lignes.filter((l) => statutDe(l) === filtre));

/** Une page demandée hors bornes retombe sur la dernière : après un filtre qui réduit
 *  la liste, l'écran ne doit pas rester sur une page vide. */
export function paginer(liste, page, taille) {
  const pages = Math.max(1, Math.ceil(liste.length / taille));
  const courante = Math.min(Math.max(1, page), pages);
  return {
    items: liste.slice((courante - 1) * taille, courante * taille),
    page: courante, pages, total: liste.length,
  };
}

/* --- La confirmation d'un geste irréversible ---------------------------------------- */

const normaliser = (texte) => String(texte || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Taper le nom de l'agrégateur pour révoquer : la casse et les accents ne comptent
 *  pas, un nom partiel ne suffit pas. */
export const nomConfirme = (saisie, nom) => (
  normaliser(saisie) !== '' && normaliser(saisie) === normaliser(nom));

/* --- La santé ------------------------------------------------------------------------ */

/** Ce qui demande une action pour CET agrégateur : les actifs déclarés qui ne tiennent
 *  pas leur promesse de mesure, et ceux pilotés par l'un et rattachés à l'autre. */
export function alertesSante(sante, divergences, agregateur) {
  const muettes = sante?.agregateurs?.[agregateur]?.detail?.muettes || [];
  const ecarts = (divergences?.divergences || [])
    .filter((d) => d.pilote_par === agregateur || d.facture_a === agregateur);
  return { muettes, ecarts, total: muettes.length + ecarts.length };
}

/* --- Les références ------------------------------------------------------------------ */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Une référence déclarée sans nom choisi vaut l'identifiant de l'actif, un UUID de 36
 *  caractères qui s'étalait sur trois lignes et noyait le titre des dialogues. On en
 *  garde le début ; la référence complète reste en infobulle, c'est elle que
 *  l'agrégateur emploie. Une référence choisie (« REF-DEB0CA94 ») passe intacte. */
export const refCourte = (ref) => (UUID.test(String(ref || '')) ? `${ref.slice(0, 8)}…` : (ref || '—'));
